<template>
  <v-sheet
    class="calendar-module main-listing-page px-3"
    style="height: calc(100vh - 88px); background: #f5f5f5"
  >
    <FullDialog
      :dialog="calendarDialog"
      :hide-close-button="true"
      v-on:close="calendarDialog = false"
      :back-route="{ name: 'admin.dashboard' }"
    >
      <template v-slot:title>
        <div class="calendar-header-bar w-100 bg-white border-bottom">
          <!-- Top Row: Navigation & Main Actions -->
          <div
            class="d-flex align-center justify-space-between px-4 py-3 flex-wrap"
            style="gap: 12px"
          >
            <!-- Left Controls: Today, Prev/Next, Month Title, View Selector -->
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <v-btn
                depressed
                color="cyan"
                class="white--text font-weight-600 rounded-md me-1"
                @click="setToday"
                :loading="pageLoading"
                :disabled="pageLoading"
              >
                <v-icon left small>mdi-calendar-today</v-icon>
                Today
              </v-btn>

              <v-btn
                fab
                outlined
                x-small
                color="grey darken-2"
                @click="prev"
                :loading="pageLoading"
                :disabled="pageLoading"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                fab
                outlined
                x-small
                color="grey darken-2"
                class="me-2"
                @click="next"
                :loading="pageLoading"
                :disabled="pageLoading"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>

              <div class="text-h6 font-weight-bold text-slate-800 me-4">
                {{ calendarTitle }}
              </div>

              <!-- View Type Menu -->
              <v-menu left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    outlined
                    color="cyan"
                    class="font-weight-600 rounded-md"
                    v-bind="attrs"
                    v-on="on"
                    :loading="pageLoading"
                    :disabled="pageLoading"
                  >
                    <v-icon left small>mdi-calendar-range</v-icon>
                    <span>{{ typeToLabel[type] }}</span>
                    <v-icon right small>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="py-0">
                  <v-list-item @click="type = 'day'">
                    <v-list-item-title>Day</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = 'week'">
                    <v-list-item-title>Week</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = '4day'">
                    <v-list-item-title>4 Days</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="type = 'month'">
                    <v-list-item-title>Month</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <!-- Right Actions: Create & Resource Calendar -->
            <div class="d-flex align-center" style="gap: 8px">
              <v-btn
                v-if="!meetingCalendar && getPermission('job:create')"
                depressed
                dark
                class="white--text custom-bold-button custom-btn-color rounded-md"
                v-on:click="typeConfirmDialog = true"
              >
                <v-icon dark left small>mdi-plus</v-icon> Create
              </v-btn>
              <v-btn
                v-if="!meetingCalendar"
                depressed
                color="cyan"
                class="white--text custom-bold-button rounded-md mr-3"
                :to="{ name: 'admin.calendar-resource' }"
                :loading="pageLoading"
                :disabled="pageLoading"
              >
                <v-icon left small>mdi-account-clock-outline</v-icon>
                Resource Calendar
              </v-btn>
              <v-tooltip bottom content-class="custom-bottom-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    @click.prevent.stop="
                      $router.push({ name: 'admin.dashboard' })
                    "
                    color="red darken-4 cursor-pointer"
                    >mdi-close</v-icon
                  >
                </template>
                <span>Close</span>
              </v-tooltip>
            </div>
          </div>

          <!-- Bottom Row: Filter Controls Bar -->
          <div
            class="d-flex align-center flex-wrap px-4 py-2 border-top"
            style="gap: 10px; background-color: #fafafa"
          >
            <div
              class="d-flex align-center me-2 text-slate-700 font-weight-600 font-size-14"
            >
              <v-icon small color="cyan" class="me-1"
                >mdi-filter-variant</v-icon
              >
              Filters:
            </div>

            <!-- Date Picker -->
            <div style="min-width: 180px; max-width: 200px">
              <DatePicker
                outlined
                depressed
                hide-details
                placeholder="Select Date"
                v-model="dateFilter"
                v-on:click:clear="clearDateFilter"
                class-name="mt-0 bg-white"
                clearable
                v-on:change="getCalendar($event)"
                :disabled="pageLoading"
                :loading="pageLoading"
              >
              </DatePicker>
            </div>

            <!-- Parent Type Dropdown (Meeting Calendar) -->
            <div
              v-if="meetingCalendar"
              style="min-width: 160px; max-width: 190px"
            >
              <v-autocomplete
                :items="meetingList"
                v-model="parentType"
                hide-details
                clearable
                filled
                flat
                solo
                dense
                placeholder="Parent Type"
                color="cyan"
                item-color="cyan"
                item-text="text"
                item-value="value"
                v-on:click:clear="
                  parentType = null;
                  getCalendar();
                "
                v-on:change="getCalendar()"
                class="pt-0 mt-0 bg-white rounded-md"
                :disabled="pageLoading"
                :loading="pageLoading"
              >
              </v-autocomplete>
            </div>

            <!-- Filter Type Dropdown -->
            <div
              v-if="!meetingCalendar && false"
              style="min-width: 140px; max-width: 175px"
            >
              <v-autocomplete
                :items="filterList"
                v-model="filter"
                hide-details
                clearable
                v-on:click:clear="getCalendar()"
                filled
                flat
                solo
                dense
                placeholder="Type"
                color="cyan"
                item-color="cyan"
                v-on:change="getCalendar()"
                class="pt-0 mt-0 bg-white rounded-md"
                :disabled="pageLoading"
                :loading="pageLoading"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      style="height: 14px; width: 14px; min-width: 14px"
                      :color="item.color"
                      class="me-2 pa-0 d-inline-block"
                    ></v-chip>
                    <span
                      class="text-capitalize font-weight-500 font-size-14 text-truncate"
                      >{{ item.text }}</span
                    >
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      style="height: 14px; width: 14px; min-width: 14px"
                      :color="item.color"
                      class="me-2 pa-0 d-inline-block"
                    ></v-chip>
                    <span
                      class="text-capitalize font-weight-500 font-size-14 text-truncate"
                      >{{ item.text }}</span
                    >
                  </div>
                </template>
              </v-autocomplete>
            </div>

            <!-- Job Type Dropdown -->
            <div
              v-if="!meetingCalendar"
              style="min-width: 200px; max-width: 200px"
            >
              <v-autocomplete
                :items="typeList"
                v-model="job_type"
                hide-details
                clearable
                filled
                flat
                v-on:click:clear="
                  job_type = null;
                  getCalendar();
                "
                :disabled="pageLoading"
                :loading="pageLoading"
                solo
                dense
                placeholder="Job Type"
                v-on:change="getCalendar()"
                color="cyan"
                item-color="cyan"
                class="pt-0 mt-0 bg-white rounded-md"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      style="height: 14px; width: 14px; min-width: 14px"
                      :color="item.color"
                      class="me-2 pa-0 d-inline-block"
                    ></v-chip>
                    <span
                      class="text-capitalize font-weight-500 font-size-14 text-truncate"
                      >{{ item.text }}</span
                    >
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      style="height: 14px; width: 14px; min-width: 14px"
                      :color="item.color"
                      class="me-2 pa-0 d-inline-block"
                    ></v-chip>
                    <span
                      class="text-capitalize font-weight-500 font-size-14 text-truncate"
                      >{{ item.text }}</span
                    >
                  </div>
                </template>
              </v-autocomplete>
            </div>

            <!-- Customer Dropdown -->
            <div
              v-if="!meetingCalendar"
              style="min-width: 170px; max-width: 200px"
            >
              <v-autocomplete
                :items="customerList"
                v-model="customer"
                hide-details
                clearable
                v-on:click:clear="
                  customer = null;
                  getCalendar();
                "
                filled
                flat
                solo
                dense
                placeholder="Select Customer"
                color="cyan"
                item-color="cyan"
                item-text="display_name"
                item-value="id"
                v-on:change="getCalendar()"
                class="pt-0 mt-0 bg-white rounded-md"
                :disabled="pageLoading"
                :loading="pageLoading"
              >
              </v-autocomplete>
            </div>

            <!-- Technician Dropdown -->
            <div
              v-if="!meetingCalendar"
              style="min-width: 180px; max-width: 210px"
            >
              <v-autocomplete
                :items="engineerList"
                v-model="engineer"
                hide-details
                clearable
                v-on:click:clear="
                  engineer = null;
                  getCalendar();
                "
                filled
                flat
                v-on:change="getCalendar()"
                :disabled="pageLoading"
                :loading="pageLoading"
                solo
                dense
                placeholder="Select Technician"
                color="cyan"
                item-color="cyan"
                item-text="display_name"
                item-value="id"
                class="pt-0 mt-0 bg-white rounded-md"
              >
              </v-autocomplete>
            </div>

            <!-- Calendar Type Dropdown -->
            <div
              v-if="!meetingCalendar"
              style="min-width: 150px; max-width: 180px"
            >
              <v-autocomplete
                :items="calendarTypeList"
                v-model="calendarType"
                hide-details
                clearable
                filled
                flat
                v-on:click:clear="
                  calendarType = null;
                  getCalendar();
                "
                v-on:change="getCalendar()"
                solo
                dense
                placeholder="Calendar Type"
                color="cyan"
                item-color="cyan"
                item-text="text"
                item-value="value"
                class="pt-0 mt-0 bg-white rounded-md"
                :disabled="pageLoading"
                :loading="pageLoading"
              >
              </v-autocomplete>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:body>
        <table width="100%" v-if="pageLoading">
          <tbody>
            <tr>
              <td colspan="3"><br /><br /><br /><br /><br /><br /></td>
            </tr>
            <tr>
              <td colspan="3"><br /><br /><br /><br /><br /><br /></td>
            </tr>
            <tr>
              <td></td>
              <td align="center" valign="middle">
                <p class="font-size-20 font-weight-600 my-4">Please wait...</p>
                <v-progress-linear
                  color="cyan"
                  indeterminate
                  rounded
                  height="10"
                ></v-progress-linear>
                <br />
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="calendar-module" v-else>
          <div class="calendar-wrapper">
            <div class="calendar-area">
              <div class="calendar pl-2" style="height: calc(100vh - 140px)">
                <v-calendar
                  ref="calendar"
                  v-model="focus"
                  style="width: 100%"
                  color="primary"
                  :events="events"
                  :event-color="getEventColor"
                  :type="type"
                  @click:event="openSingledata"
                  @click:more="showAllEvent"
                  @click:date="viewDay"
                  @change="updateRange"
                  :weekdays="cWeekdays"
                >
                  <template v-slot:day="day">
                    <div :class="`d-none day-${day.weekday}`"></div>
                  </template>
                  <template v-slot:event="{ event }">
                    <template v-if="event.type != 'visit'">
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            small
                            v-bind="attrs"
                            v-on="on"
                            style="width: 97%"
                            :color="event.color"
                            class="ml-1 mb-2"
                            label
                            outlined
                          >
                            <span
                              class="font-size-10 font-weight-500 text-capitalize"
                            >
                              {{
                                event.type == "meeting"
                                  ? event.parent_barcode
                                    ? `${event.parent_barcode}${
                                        event.parent_name
                                          ? ` - ${event.parent_name}`
                                          : ""
                                      } - ${event.Title || event.name}`
                                    : `${
                                        event.parent_name
                                          ? `${event.parent_name} - `
                                          : ""
                                      }${event.Title || event.name}`
                                  : getEvents(event)
                              }}
                            </span>
                          </v-chip>
                        </template>
                        <div
                          v-if="event.type == 'meeting'"
                          class="text-left pa-2"
                        >
                          <div class="font-weight-bold mb-1">
                            {{ event.Title || event.name }}
                          </div>
                          <div>
                            Parent: {{ event.parent_type }}
                            <strong>{{ event.parent_barcode || "—" }}</strong>
                          </div>
                          <div v-if="event.parent_name">
                            {{
                              event.parent_type === "opportunity"
                                ? "Opportunity"
                                : "Lead"
                            }}:
                            {{ event.parent_name }}
                          </div>
                          <div>Meeting: {{ event.name }}</div>
                          <div v-if="event.meeting_type">
                            Type: {{ event.meeting_type }}
                          </div>
                          <div>
                            Date: {{ event.start }}
                            <template v-if="event.start_time">
                              at {{ event.start_time }}
                            </template>
                          </div>
                        </div>
                        <span v-else> {{ event.status_text }}</span>
                      </v-tooltip>
                    </template>
                    <div v-else class="d-flex">
                      <div>
                        <v-tooltip top content-class="custom-top-tooltip">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              small
                              v-bind="attrs"
                              v-on="on"
                              :color="event.priority_color"
                              class="ml-1 text-white mt-n1"
                              label
                              style="width: 10px !important"
                            >
                            </v-chip>
                          </template>
                          <span>Priority</span>
                        </v-tooltip>
                      </div>
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            small
                            v-bind="attrs"
                            v-on="on"
                            style="width: 97%"
                            :color="event.color"
                            :text-color="event.status == 2 ? 'black' : 'white'"
                            class="ml-1 text-white"
                            label
                          >
                            <span
                              class="font-size-10 font-weight-500 text-capitalize"
                            >
                              {{ getEvents(event) }}
                            </span>
                          </v-chip>
                        </template>
                        <div class="text-left pa-2">
                          <div class="font-weight-bold mb-1">
                            {{ event.Title || event.name }}
                          </div>
                          <div class="mb-1">
                            <v-icon small dark class="mr-1"
                              >mdi-clock-outline</v-icon
                            >
                            {{ event.start
                            }}<template v-if="event.end">
                              to {{ event.end }}</template
                            >
                          </div>
                          <div v-if="event.customer" class="mb-1">
                            <v-icon small dark class="mr-1">mdi-domain</v-icon>
                            {{ event.customer }}
                          </div>
                          <div v-if="event.contact_person" class="mb-1">
                            <v-icon small dark class="mr-1">mdi-account</v-icon>
                            {{ event.contact_person }}
                            <template v-if="event.contact_phone">
                              ({{ event.contact_phone }})</template
                            >
                          </div>
                          <div
                            v-if="event.site_location"
                            class="mb-1 d-flex align-start"
                          >
                            <v-icon small dark class="mr-1 mt-1"
                              >mdi-map-marker</v-icon
                            >
                            {{ event.site_location }}
                          </div>
                          <div
                            v-if="event.technicians && event.technicians.length"
                            class="mb-1"
                          >
                            <v-icon small dark class="mr-1">mdi-tools</v-icon>
                            {{ event.technicians.join(", ") }}
                          </div>
                          <div v-if="event.admin_note" class="mb-1 d-flex">
                            <v-icon small dark class="mr-1 mt-1"
                              >mdi-note-text</v-icon
                            >
                            <span
                              class="d-inline-block text-truncate"
                              style="max-width: 200px"
                            >
                              {{ event.admin_note }}
                            </span>
                          </div>
                          <div
                            v-if="event.status_text"
                            class="mt-2 pt-2"
                            style="
                              border-top: 1px solid rgba(255, 255, 255, 0.2);
                            "
                          >
                            Status: {{ event.status_text }}
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                  </template>
                </v-calendar>

                <v-menu
                  v-model="moreOpen"
                  :close-on-content-click="false"
                  :activator="moreElement"
                  offset-y
                  top
                  left
                >
                  <v-card
                    class="grey pa-2 lighten-4"
                    min-width="350px"
                    max-width="350px"
                    style="height: 600px"
                  >
                    <v-card-text
                      class="white--text mb-1"
                      style="
                        color: red !important;
                        font-size: 22px !important;
                        font-weight: 500;
                        margin-left: -10px !important;
                      "
                    >
                      {{ formatDate(moreData[0]?.start) }}
                    </v-card-text>
                    <template v-for="(row, index) in moreData">
                      <v-card-text
                        :class="`${row.color} white--text mb-1 pa-1`"
                        :key="index"
                        v-on:click="openSingledataDoublePopup(row)"
                      >
                        <span v-if="row.type === 'meeting'">
                          {{ row.parent_barcode || "" }}
                          <template v-if="row.parent_name">
                            - {{ row.parent_name }}
                          </template>
                          <template v-if="row.Title || row.name">
                            - {{ row.Title || row.name }}
                          </template>
                        </span>
                        <span v-else>{{ getEvents(row) }}</span>
                      </v-card-text>
                    </template>
                  </v-card>
                </v-menu>
              </div>
            </div>

            <div class="sidebar white" v-if="toggleVisit">
              <div class="position-sticky grey lighten-2">
                <div class="d-flex py-1">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <h5
                        class="mt-2 py-0 pl-2 cursor-pointer"
                        style="border-color: #a3a3a3 !important"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ filterType }}
                        <v-icon size="28">mdi-menu-down</v-icon>
                      </h5>
                    </template>
                    <v-list>
                      <v-list-item-group color="primary">
                        <v-list-item link @click="filterType = 'All visit'">
                          <v-list-item-title
                            class="fw-600 d-flex"
                            v-on:click="getVisitList('', 'all')"
                          >
                            <span
                              class="white--text mr-2 rounded-circle blue d-flex justify-center align-center"
                              style="height: 22px; width: 22px"
                            ></span>
                            All Visit</v-list-item-title
                          >
                        </v-list-item>
                        <v-list-item
                          link
                          @click="filterType = 'Assigned Visit'"
                        >
                          <v-list-item-title
                            class="fw-600 d-flex"
                            v-on:click="getVisitList('', 'assigned')"
                          >
                            <span
                              class="white--text mr-2 rounded-circle green d-flex justify-center align-center"
                              style="height: 22px; width: 22px"
                            ></span>
                            Assigned Visit</v-list-item-title
                          >
                        </v-list-item>
                        <v-list-item
                          link
                          @click="filterType = 'UnAssigned Visit'"
                        >
                          <v-list-item-title
                            class="fw-600 d-flex"
                            v-on:click="getVisitList('', 'ussigned')"
                          >
                            <span
                              class="white--text mr-2 rounded-circle orange d-flex justify-center align-center"
                              style="height: 22px; width: 22px"
                            ></span>
                            UnAssigned Visit</v-list-item-title
                          >
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-menu>
                  <v-spacer></v-spacer>
                  <v-btn
                    fab
                    icon
                    small
                    @click="showCalendarFilter = !showCalendarFilter"
                  >
                    <v-icon>mdi-calendar-check-outline</v-icon>
                  </v-btn>
                </div>
                <div v-if="showCalendarFilter" class="mb-2">
                  <v-date-picker
                    full-width
                    no-title
                    color="green lighten-1"
                    v-model="datePicker"
                    v-on:change="getVisitList(datePicker)"
                  ></v-date-picker>
                </div>
              </div>
              <div class="event-list px-3">
                <v-list
                  v-if="visitArr && visitArr.length"
                  class="py-0 grey lighten-4"
                >
                  <v-list-item-group
                    v-model="selected"
                    active-class="blue lighten-4"
                  >
                    <template v-for="(item, index) in visitArr">
                      <v-list-item
                        :key="index"
                        class="mb-2 white px-2 border-red"
                        :style="`border-left: ${getPriorityColor(
                          item.ticket_calendar.priority_text
                        )}; box-shadow: 0 2px 10px 0px rgba(0, 0, 0, 0.1); background-color: ${getBgColor(
                          item.status
                        )}`"
                      >
                        <template v-slot:default="{ active }">
                          <v-list-item-content
                            v-on:click="getCalendar('', item.id)"
                          >
                            <v-list-item-title
                              :class="`blue--text mb-1`"
                              class="fw-700 d-flex align-center"
                            >
                              #{{ item.barcode }}
                              <v-spacer></v-spacer>
                              <span
                                :class="`${getColor(item.status)}--text fw-500`"
                              >
                                <span class="d-block">
                                  {{ formatDate(item.started_at) }}
                                  {{ formatDateTimeStart(item.started_at) }}
                                </span>
                              </span>
                            </v-list-item-title>
                            <v-list-item-title
                              style="font-size: 16px"
                              class="fw-600"
                              :class="`${
                                active ? getColor(item.status) : ''
                              }--text mb-1`"
                              v-text="item.Title"
                            ></v-list-item-title>
                            <v-list-item-subtitle
                              class="text--primary fw-500"
                              v-text="item.customer.display_name"
                            ></v-list-item-subtitle>

                            <template v-if="item.ticket_calendar.type == 1">
                              One Off Job
                            </template>
                            <template v-if="item.ticket_calendar.type == 2">
                              Recurring Job
                            </template>
                          </v-list-item-content>

                          <v-list-item-action>
                            <v-icon
                              v-if="item.follow_up > 0"
                              color="red"
                              size="18"
                              class="my-1"
                            >
                              mdi-flag</v-icon
                            >
                            <v-icon v-else size="18" class="my-1">
                              mdi-flag</v-icon
                            >
                          </v-list-item-action>
                        </template>
                      </v-list-item>
                    </template>
                  </v-list-item-group>
                </v-list>
                <div v-else>
                  <div class="no-date text-center pa-5 mt-5">
                    <img
                      :src="$assetURL('media/error/empty.png')"
                      class="row-not-found-image"
                    />
                    <div class="text-h6 orange--text text--lighten-2 pt-3">
                      Uhh... There are no visits at the moment.
                    </div>
                  </div>
                </div>
                <v-menu
                  v-model="eventPopover"
                  :close-on-click="false"
                  :close-on-content-click="false"
                  :position-x="positionX"
                  :position-y="positionY"
                  absolute
                  offset-y
                  tile
                  elevation="0"
                  min-width="350"
                >
                  <v-card
                    color=""
                    min-width="300px"
                    max-width="350px"
                    elevation="0"
                    class="all-event-wrapper"
                  >
                    <v-toolbar color="green" dark height="40px" class="">
                      <v-toolbar-title>Lorem, ipsum dolor.</v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-icon
                        class="cursor-pointer"
                        size="18"
                        v-on:click="openSingledata($event)"
                        >mdi-pencil</v-icon
                      >
                      <v-icon
                        class="cursor-pointer ml-4"
                        size="18"
                        @click="eventPopover = false"
                        >mdi-close</v-icon
                      >
                    </v-toolbar>
                  </v-card>
                </v-menu>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:action>
        <div
          class="legends d-flex flex-wrap align-center"
          v-if="filter != 'quotation' && filter != 'project'"
          style="gap: 8px"
        >
          <div
            class="px-3 py-1 me-2 d-flex align-center border rounded-pill bg-light-cyan"
            style="border-color: #00c2c7 !important"
          >
            <v-icon size="14" color="cyan" class="me-1">mdi-circle</v-icon>
            <span class="text-slate-800 font-weight-700 caption">
              {{ meetingCalendar ? "Sales :" : "Visit :" }}
            </span>
          </div>
          <div
            v-if="meetingCalendar"
            class="px-3 py-1 d-flex align-center border rounded-pill bg-white"
            style="border-color: #1976d2 !important"
          >
            <v-icon size="12" color="blue" class="me-1">mdi-circle</v-icon>
            <span class="text-slate-700 caption font-weight-600 me-3"
              >Leads</span
            >
            <v-icon size="12" color="yellow" class="me-1">mdi-circle</v-icon>
            <span class="text-slate-700 caption font-weight-600"
              >Opportunity</span
            >
          </div>
          <template v-if="!meetingCalendar">
            <div
              class="px-3 py-1 d-flex align-center border rounded-pill bg-white"
              v-for="(row, index) in statusList"
              :key="index"
              :style="`border-color: ${getStatusColor(row.color)} !important;`"
              :class="{
                'status-filter-selected':
                  status !== null &&
                  status !== undefined &&
                  status !== '' &&
                  Number(status) === Number(row.status),
                'cursor-pointer': !pageLoading,
              }"
              role="button"
              :tabindex="pageLoading ? -1 : 0"
              :aria-pressed="
                status !== null &&
                status !== undefined &&
                status !== '' &&
                Number(status) === Number(row.status)
              "
              :aria-disabled="pageLoading"
              @click="selectStatus(row.status)"
              @keydown.enter.prevent="selectStatus(row.status)"
              @keydown.space.prevent="selectStatus(row.status)"
            >
              <v-icon size="12" :color="row.color" class="me-1"
                >mdi-circle</v-icon
              >
              <span
                class="text-slate-700 caption font-weight-600"
                :class="{
                  'status-filter-label-selected':
                    status !== null &&
                    status !== undefined &&
                    status !== '' &&
                    Number(status) === Number(row.status),
                }"
                >{{ row.text }}</span
              >
            </div>
          </template>
        </div>
      </template>
    </FullDialog>

    <Dialog :common-dialog="typeConfirmDialog">
      <template v-slot:title>Confirmation</template>
      <template v-slot:body>
        <v-container fluid class="py-0">
          <v-row>
            <v-col lg="12">
              <RadioInputCustom
                v-model="jobType"
                row
                color="blue darken-4"
                :items="[
                  {
                    label: 'Adhoc Visit',
                    value: 'one-off',
                  },
                  {
                    label: 'Recurring Visit',
                    value: 'recurring',
                  },
                ]"
              ></RadioInputCustom>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button white--text"
          color="cyan"
          v-on:click="pushToVisit()"
        >
          Continue
        </v-btn>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="typeConfirmDialog = false"
        >
          Close
        </v-btn>
      </template>
    </Dialog>

    <CreateEvent
      v-if="false"
      :dialog="viewVisitDialog"
      v-on:close="viewVisitDialog = false"
    ></CreateEvent>

    <SingleVisitDetail
      v-if="viewVisitDialog"
      :visit-dialog="viewVisitDialog"
      :visit="visitId"
      type="simple"
      v-on:close="viewVisitDialog = false"
      v-on:reload="() => getCalendar()"
      v-on:reload-calendar="() => getCalendar()"
    ></SingleVisitDetail>
    <SingleDeliveryDetail
      v-if="viewDeliveryDialog"
      :visit-dialog="viewDeliveryDialog"
      :visit="deliveryId"
      v-on:close="viewDeliveryDialog = false"
    ></SingleDeliveryDetail>
    <SingleQuotationDetail
      v-if="viewQuotationDialog"
      :visit-dialog="viewQuotationDialog"
      :visit="quotationId"
      v-on:close="viewQuotationDialog = false"
    ></SingleQuotationDetail>
    <SingleLeaveDetail
      v-if="viewLeaveDialog"
      :visit-dialog="viewLeaveDialog"
      :visit="leaveId"
      v-on:close="viewLeaveDialog = false"
    ></SingleLeaveDetail>
    <SingleContractDetail
      v-if="viewContractDialog"
      :visit-dialog="viewContractDialog"
      :visit="contractId"
      v-on:close="viewContractDialog = false"
    ></SingleContractDetail>
    <SingleProjectDetail
      v-if="viewProjectDialog"
      :visit-dialog="viewProjectDialog"
      :visit="projectId"
      v-on:close="viewProjectDialog = false"
    ></SingleProjectDetail>
  </v-sheet>
</template>

<style>
.v-calendar-weekly__day:hover {
  background-color: #d7e0e1 !important;
}
</style>

<script>
import { QUERY } from "@/core/services/store/request.module";
import CreateEvent from "@/view/pages/calendar/CreateEvent";
import DatePicker from "@/view/components/DatePicker";
import moment from "moment-timezone";
import SingleQuotationDetail from "@/view/pages/job/partials/Single-Quotation-Detail.vue";
import SingleContractDetail from "@/view/pages/job/partials/Single-Contract-Detail.vue";
import SingleProjectDetail from "@/view/pages/job/partials/Single-Project-Detail.vue";
import SingleLeaveDetail from "@/view/pages/job/partials/Single-Leave-Detail.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import SingleVisitDetail from "@/view/pages/job/partials/Single-Visit-Detail.vue";
import SingleDeliveryDetail from "@/view/pages/delivery-job/partials/Single-Visit-Detail.vue";
import FullDialog from "@/view/components/FullDialog2.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import RadioInputCustom from "@/view/components/RadioInputCustom.vue";
import { toSafeInteger } from "lodash";
import APIService from "@/core/services/api.service";
import VuetifyColors from "vuetify/lib/util/colors";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  name: "Calendar",
  title: "Calendar",
  mixins: [CommonMixin],
  props: {
    meetingCalendar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      calendarDialog: true,
      typeConfirmDialog: false,
      jobType: "one-off",
      toggleVisit: false,
      cWeekdays: [1, 2, 3, 4, 5, 6, 0],
      filterType: "All visit",
      calendarFilterType: "Assigned Visit",
      showCalendarFilter: false,
      pageTitle: "Calender",
      search: null,
      pageLoading: false,
      moreData: [],
      viewVisitDialog: false,
      viewDeliveryDialog: false,
      viewContractDialog: false,
      viewQuotationDialog: false,
      viewLeaveDialog: false,
      datePicker: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      selected: false,
      eventPopover: false,
      positionX: 0,
      positionY: 0,
      eventspopup: null,

      focus: "",
      calendarTitle: "",
      type: "month",
      typeToLabel: {
        month: "Month",
        week: "Week",
        day: "Day",
        "4day": "4 Days",
      },
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      moreElement: null,
      moreOpen: false,
      viewProjectDialog: false,
      visitId: 0,
      deliveryId: 0,
      contractId: 0,
      projectId: 0,
      quotationId: 0,
      leaveId: 0,
      status: null,
      events: [],
      settingsData: [],
      visitArr: [],
      job_type: null,
      colors: [
        "red lighten-1",
        "green lighten-1",
        "blue lighten-1",
        "orange lighten-1",
        "pink lighten-1",
      ],
      names: [
        "Meeting",
        "Holiday",
        "PTO",
        "Travel",
        "Event",
        "Birthday",
        "Conference",
        "Party",
      ],
      arrayEvents: null,
      filter: null,
      dateFilter: null,
      calendarType: null,
      parentType: null,
      customer: 0,
      engineer: 0,
      statusList: [],
      customerList: [],
      engineerList: [],
      calendarTypeList: [
        { text: "Visit", value: "visit", color: "cyan" },
        { text: "Quotation", value: "quotation", color: "green" },
        { text: "Leave", value: "leave", color: "red" },
        { text: "Project", value: "project", color: "blue" },
      ],
      meetingList: [
        { text: "Leads", value: "lead" },
        { text: "Opportunities", value: "opportunity" },
      ],
      typeList: [
        {
          text: "One Off Job",
          value: "ono_off",
          color: "red lighten-1",
        },
        {
          text: "Recurring Job",
          value: "recurring",
          color: "green lighten-1",
        },
      ],
      filterList: [
        {
          text: "Visit",
          value: "visit",
          color: "#73d7d2",
        },
        {
          text: "Quotation",
          value: "quotation",
          color: "green lighten-1",
        },
        {
          text: "Contract",
          value: "contract",
          color: "orange lighten-1",
        },
        {
          text: "Project",
          value: "project",
          color: "blue lighten-1",
        },
        {
          text: "Delivery",
          value: "delivery",
          color: "purple lighten-1",
        },
      ],
    };
  },
  methods: {
    updateCalendarTitle() {
      if (this.$refs.calendar) {
        this.calendarTitle = this.$refs.calendar.title;
      }
    },
    getStatusColor(color) {
      if (typeof color !== "string") {
        return color;
      }

      const [colorName, ...shadeParts] = color.trim().split(/\s+/);
      const paletteName = colorName.replace(/-([a-z])/g, (_, letter) =>
        letter.toUpperCase()
      );
      const palette = VuetifyColors[paletteName];
      const shade = shadeParts.join("").replace(/-/g, "");

      return (palette && (palette[shade] || palette.base)) || color;
    },
    selectStatus(status) {
      if (this.pageLoading) {
        return;
      }

      const selectedStatus = Number(status);
      this.status =
        Number(this.status) === selectedStatus ? null : selectedStatus;
      this.getCalendar();
    },
    clearDateFilter() {
      this.dateFilter = null;
      this.getCalendar(null);
    },
    pushToVisit() {
      this.typeConfirmDialog = false;
      this.$router.push(
        this.getDefaultRoute("job.create", {
          query: {
            type: this.jobType,
          },
        })
      );
    },
    calendarStyle() {
      this.$nextTick(() => {
        const elem = document.querySelectorAll(".day-0,.day-6");
        elem.forEach((el) => {
          if (!el.parentElement.classList.contains("v-outside")) {
            el.parentElement.style.background = "#f1f1f1";
          }
        });
      });
    },
    formatDayCell(param) {
      console.log(param);
    },
    setMenuPosition(e) {
      e.preventDefault();
      this.positionX = e.clientX + 50;
      this.positionY = e.clientY;
      this.$nextTick(() => {
        this.eventPopover = true;
      });
    },
    getEvents(eventData) {
      let settingData = this.lodash.find(
        this.settingsData,
        (row) => row.type.toLowerCase() == eventData.type.toLowerCase()
      );
      if (settingData && settingData.value) {
        let stringArr = [];
        this.lodash.forEach(settingData.value, (row) => {
          if (eventData && eventData[row]) {
            stringArr.push(eventData[row]);
          }
        });
        return stringArr.join("/");
      }
    },
    initFilter() {
      APIService.query("resource-calendar/filters")
        .then(({ data }) => {
          this.customerList = data.customers;
          this.statusList = data.statuses;
          this.statusList.shift(1);
          this.engineerList = data.engineers;
        })
        .catch((error) => {
          this.logError(error);
        });
    },
    openSingledata(data) {
      const event =
        data && data.eventParsed && data.eventParsed.input
          ? data.eventParsed.input
          : data.event;

      if (event && event.id) {
        if (event.type == "visit") {
          this.visitId = toSafeInteger(event.id);
          this.viewVisitDialog = true;
        } else if (event.type == "meeting") {
          this.openMeetingParent(event);
        } else if (event.type == "contract") {
          this.contractId = toSafeInteger(event.id);
          this.viewContractDialog = true;
        } else if (event.type == "quotation") {
          this.quotationId = toSafeInteger(event.id);
          this.viewQuotationDialog = true;
        } else if (event.type == "Leave") {
          this.leaveId = toSafeInteger(event.id);
          this.viewLeaveDialog = true;
        } else if (event.type == "delivery") {
          this.deliveryId = toSafeInteger(event.id);
          this.viewDeliveryDialog = true;
        } else if (event.type == "project") {
          this.projectId = toSafeInteger(event.id);
          this.viewProjectDialog = true;
        }
      }
    },
    openSingledataDoublePopup(data) {
      if (data && data.id) {
        if (data.type == "visit") {
          this.visitId = toSafeInteger(data.id);
          this.viewVisitDialog = true;
        } else if (data.type == "meeting") {
          this.openMeetingParent(data);
        } else if (data.type == "contract") {
          this.contractId = toSafeInteger(data.id);
          this.viewContractDialog = true;
        } else if (data.type == "quotation") {
          this.quotationId = toSafeInteger(data.id);
          this.viewQuotationDialog = true;
        } else if (data.type == "Leave") {
          this.leaveId = toSafeInteger(data.id);
          this.viewLeaveDialog = true;
        } else if (data.type == "delivery") {
          this.deliveryId = toSafeInteger(data.id);
          this.viewDeliveryDialog = true;
        } else if (data.type == "project") {
          this.projectId = toSafeInteger(data.id);
          this.viewProjectDialog = true;
        }
      }
    },
    openMeetingParent(event) {
      const parentId = toSafeInteger(
        event.parent_id ||
          (event.parent_type === "opportunity"
            ? event.opportunity_id
            : event.lead_id)
      );
      if (!parentId) {
        return;
      }

      this.$router.push({
        name:
          event.parent_type === "opportunity"
            ? "admin.opportunity.detail"
            : "admin.lead.detail",
        params: { id: parentId },
      });
    },
    getColor(data) {
      if (data == 1) {
        return "blue";
      } else if (data == 2) {
        return "purple";
      } else if (data == 3) {
        return "red";
      } else if (data == 4) {
        return "green";
      } else if (data == 5) {
        return "red";
      } else if (data == 6) {
        return "cyan";
      }
    },
    getPriorityColor(data) {
      if (data == "Low") {
        return "5px solid green !important";
      } else if (data == "Medium") {
        return "5px solid orange !important";
      } else if (data == "High") {
        return "5px solid red !important";
      }
    },
    getBgColor(data) {
      if (data == 1) {
        return "#E3F2FD  !important";
      } else if (data == 2) {
        return "#f9f9ce !important";
      } else if (data == 3) {
        return "#f3e6cb !important";
      } else if (data == 4) {
        return "#E8F5E9 !important";
      } else if (data == 5) {
        return "#FFEBEE !important;";
      } else if (data == 6) {
        return "#d9fffd !important";
      }
    },
    formatDateTimeStart(row) {
      return moment(row).format("hh:mm A");
    },
    formatetimedata(row) {
      return moment(row).format("hh:mm A");
    },
    viewDay({ date }) {
      this.focus = date;
      this.type = "day";
    },
    getEventColor(event) {
      return event.color;
    },
    setToday() {
      this.focus = "";
    },
    prev() {
      this.$refs.calendar.prev();
      this.calendarStyle();
    },
    next() {
      this.$refs.calendar.next();
      this.calendarStyle();
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        );
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    showAllEvent({ nativeEvent }) {
      this.selectedElement = nativeEvent.target;
      const date = this.selectedElement.getAttribute("data-date");
      const open = () => {
        this.moreElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.moreOpen = true))
        );
      };

      if (this.moreOpen) {
        this.moreOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }

      nativeEvent.stopPropagation();
      this.getPopupData(date);
      if (!this.meetingCalendar) {
        this.getPopupVisit(date);
      }
    },
    getPopupVisit(date) {
      const _this = this;

      _this.$store
        .dispatch(QUERY, {
          url: "popup-calendar",
          data: { start_date: date },
        })
        .then((data) => {
          _this.eventspopup = data;
        });
    },
    updateRange({ start, end }) {
      const events = [];

      const min = new Date(`${start.date}T00:00:00`);
      const max = new Date(`${end.date}T23:59:59`);
      const days = (max.getTime() - min.getTime()) / 86400000;
      const eventCount = this.rnd(days, days + 20);

      for (let i = 0; i < eventCount; i++) {
        const allDay = this.rnd(0, 3) === 0;
        const firstTimestamp = this.rnd(min.getTime(), max.getTime());
        const first = new Date(firstTimestamp - (firstTimestamp % 900000));
        const secondTimestamp = this.rnd(2, allDay ? 288 : 8) * 900000;
        const second = new Date(first.getTime() + secondTimestamp);

        events.push({
          name: this.names[this.rnd(0, this.names.length - 1)],
          start: first,
          end: second,
          color: this.colors[this.rnd(0, this.colors.length - 1)],
          timed: !allDay,
        });
      }

      this.$nextTick(() => this.updateCalendarTitle());
    },
    rnd(a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a;
    },
    getVisitList(datePicker, type) {
      const _this = this;
      _this.pageLoading = true;
      return new Promise((resolve, reject) => {
        try {
          _this.$store
            .dispatch(QUERY, {
              url: "visit-list",
              data: {
                date: datePicker,
                type: type,
                search: this.search,
              },
            })
            .then(({ data }) => {
              this.visitArr = data;
              resolve(data);
            })
            .catch((error) => {
              reject(error);
            })
            .finally(() => {
              _this.pageLoading = false;
            });
        } catch (error) {
          reject(error);
        }
      });
    },
    getCalendar(date, visit) {
      const _this = this;
      if (date === true || date === false || typeof date !== "string") {
        date = this.dateFilter;
      }
      _this.pageLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "all/calendar",
          data: {
            date: date ? date : null,
            visit_id: visit ? visit : null,
            filter: this.meetingCalendar
              ? "meeting"
              : this.calendarType
              ? this.calendarType
              : this.filter,
            parent_type: this.meetingCalendar ? this.parentType : null,
            job_type: this.job_type ? this.job_type : null,
            customer: this.customer ? this.customer : null,
            engineer: this.engineer ? this.engineer : null,
            status: this.status ? this.status : null,
          },
        })
        .then(({ data }) => {
          _this.events = data.row;
          _this.settingsData = data.settings;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    getPopupData(date) {
      const _this = this;
      _this.$store
        .dispatch(QUERY, {
          url: "all/calendar",
          data: {
            date: date,
            filter: this.meetingCalendar ? "meeting" : null,
            parent_type: this.meetingCalendar ? this.parentType : null,
          },
        })
        .then((data) => {
          _this.moreData = data.data.row;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    functionEvents(date) {
      const [, , day] = date.split("-");
      if ([12, 17, 28].includes(parseInt(day, 10))) return true;
      if ([1, 19, 22].includes(parseInt(day, 10))) return ["red", "#00f"];
      return false;
    },
    genrateEvent() {
      const event = [...Array(36)].map(() => {
        const day = Math.floor(Math.random() * 30);
        const d = new Date();
        d.setDate(day);
        return d.toISOString().substr(0, 10);
      });
      return event;
    },
  },
  components: {
    FullDialog,
    Dialog,
    RadioInputCustom,
    CreateEvent,
    DatePicker,
    SingleVisitDetail,
    SingleQuotationDetail,
    SingleLeaveDetail,
    SingleContractDetail,
    SingleDeliveryDetail,
    SingleProjectDetail,
  },
  watch: {
    pageLoading(isLoading) {
      if (!isLoading) {
        this.$nextTick(() => this.updateCalendarTitle());
      }
    },
  },
  mounted() {
    this.arrayEvents = this.genrateEvent();
    this.getCalendar();
    if (!this.meetingCalendar) {
      this.getVisitList();
      this.initFilter();
    }
    this.$nextTick(() => {
      this.calendarStyle();
    });
  },
};
</script>

<style scoped>
.calendar-header-bar {
  border-bottom: 1px solid #eef2f6;
}

.status-filter-selected {
  border-width: 2px !important;
}

.text-slate-700.status-filter-label-selected {
  color: #0f172a !important;
  font-weight: 700 !important;
}

.text-slate-800 {
  color: #1e293b;
}
.text-slate-700 {
  color: #334155;
}
.bg-light-cyan {
  background-color: #e6fffb;
}
.shadow-xs {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}
.all-event-wrapper th,
.all-event-wrapper td {
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 4px;
  padding-right: 4px;
}
.all-event-wrapper th {
  font-weight: 500;
}
.custom-top-tooltip {
  max-width: 500px !important;
  white-space: normal !important;
}
</style>
